import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash, PencilSimple, Plus, X } from "@phosphor-icons/react";
import { ImageUpload } from "@/components/ImageUpload";
import { PostProductPicker } from "@/components/PostProductPicker";
import { ClicksPanel } from "@/components/ClicksPanel";
import { GalleryField } from "@/components/GalleryField";
import { ListItemNotes } from "@/components/ListItemNotes";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
} from "recharts";

function ArrayField({ label, value, onChange, testid }) {
  const [text, setText] = useState((value || []).join("\n"));
  const joined = (value || []).join("\n");
  useEffect(() => {
    setText((t) => (t.split("\n").filter((s) => s.trim()).join("\n") === joined ? t : joined));
  }, [joined]);
  return (
    <div>
      <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">{label} (one per line)</label>
      <textarea
        data-testid={testid}
        rows={3}
        value={text}
        onChange={(e) => { setText(e.target.value); onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean)); }}
        className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5B7B53]/30"
      />
    </div>
  );
}

const EMPTY = {
  title: "", category: "", short_description: "", long_description: "",
  image_url: "", price: "", rating: 4.5, review_count: 0, asin: "",
  external_url: "", pros: [], cons: [], features: [], benefits: [], gallery: [], featured: false, tags: [],
};

export function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <p className="text-[#5C5A56]">Loading dashboard...</p>;

  const s = data.stats;
  const tiles = [
    { label: "Products", value: s.products, testid: "stat-products" },
    { label: "Curated lists", value: s.lists, testid: "stat-lists" },
    { label: "Blog posts", value: s.posts, testid: "stat-posts" },
    { label: "Categories", value: s.categories, testid: "stat-categories" },
    { label: "Featured picks", value: s.featured_products, testid: "stat-featured" },
    { label: "Newsletter subscribers", value: s.subscribers, testid: "stat-newsletter-subscribers" },
  ];

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-2">Overview</p>
      <h1 className="font-display text-4xl text-[#2C2B29] mb-10">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-10">
        {tiles.map((t) => (
          <div key={t.label} data-testid={t.testid} className="border border-[#E5E0D8] bg-white rounded-xl p-5">
            <p className="text-[10px] uppercase tracking-wider text-[#5C5A56]">{t.label}</p>
            <p className="font-display text-3xl text-[#2C2B29] mt-1.5">{t.value}</p>
          </div>
        ))}
      </div>

      <ClicksPanel />

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div className="border border-[#E5E0D8] bg-white rounded-2xl p-6">
          <h3 className="font-display text-xl text-[#2C2B29] mb-1">Products by category</h3>
          <p className="text-xs text-[#5C5A56] mb-5">Distribution of your catalog</p>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={data.by_category} margin={{ top: 5, right: 12, left: -14, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
                <XAxis dataKey="category" tick={{ fontSize: 10, fill: "#5C5A56" }} interval={0} angle={-18} textAnchor="end" height={64} />
                <YAxis tick={{ fontSize: 11, fill: "#5C5A56" }} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#EADDC8", opacity: 0.4 }} contentStyle={{ borderRadius: 8, border: "1px solid #E5E0D8", fontSize: 12 }} />
                <Bar dataKey="count" fill="#5B7B53" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-[#E5E0D8] bg-white rounded-2xl p-6">
          <h3 className="font-display text-xl text-[#2C2B29] mb-1">Newsletter signups</h3>
          <p className="text-xs text-[#5C5A56] mb-5">Last 6 months</p>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={data.subscribers_series} margin={{ top: 5, right: 12, left: -14, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#5C5A56" }} />
                <YAxis tick={{ fontSize: 11, fill: "#5C5A56" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E0D8", fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#D17A58" strokeWidth={2.5} dot={{ fill: "#D17A58", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <RecentPanel title="Recent products" rows={data.recent_products} labelKey="category" />
        <RecentPanel title="Recent lists" rows={data.recent_lists} labelKey="category" />
        <RecentPanel title="Recent posts" rows={data.recent_posts} labelKey="author" />
      </div>

      <IndexNowPanel />
    </div>
  );
}

function IndexNowPanel() {
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get("/indexnow/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/indexnow/submit-all");
      setLastResult(data);
      if (data.submitted) {
        toast.success(`Submitted ${data.total_urls} URLs to IndexNow (Bing / Yandex)`);
      } else {
        toast.warning(data.reason || "Submission skipped");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-10 border border-[#E5E0D8] bg-white rounded-2xl p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-xl text-[#2C2B29]">Search engine indexing</h3>
          <p className="text-xs text-[#5C5A56] mt-1 max-w-lg">
            IndexNow instantly notifies Bing, Yandex, and Seznam whenever you publish or edit content.
            Also runs automatically on every product / post save.
          </p>
        </div>
        <button
          data-testid="indexnow-submit-all"
          onClick={submit}
          disabled={submitting || !status?.configured}
          className="hh-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit all URLs"}
        </button>
      </div>
      <div className="mt-4 text-xs text-[#5C5A56] grid sm:grid-cols-2 gap-3">
        <div>
          <span className="uppercase tracking-wider">Status</span>
          <p className="text-[#2C2B29] mt-1">
            {status == null ? "Loading..." : status.configured ? "✓ Configured" : "Not configured (add INDEXNOW_KEY, SITE_HOST, PUBLIC_SITE_URL to backend .env)"}
          </p>
        </div>
        <div>
          <span className="uppercase tracking-wider">Key location</span>
          <p className="text-[#2C2B29] mt-1 break-all font-mono text-[11px]">{status?.key_location || "—"}</p>
        </div>
      </div>
      {lastResult ? (
        (() => {
          const sc = lastResult.status_code;
          const ok = sc && sc >= 200 && sc < 300;
          return (
            <p className={`text-xs mt-3 ${ok ? "text-[#5B7B53]" : "text-[#D17A58]"}`}>
              Last submit: {lastResult.total_urls} URLs · HTTP {sc || "—"} {ok ? "✓" : "· not accepted"}
            </p>
          );
        })()
      ) : null}
    </div>
  );
}

function RecentPanel({ title, rows, labelKey }) {
  return (
    <div className="border border-[#E5E0D8] bg-white rounded-2xl p-6">
      <h3 className="font-display text-lg text-[#2C2B29] mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-[#5C5A56]">No items yet.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id} className="text-sm border-b border-[#E5E0D8] pb-3 last:border-0 last:pb-0">
              <p className="text-[#2C2B29] font-medium leading-snug">{r.title}</p>
              <p className="text-xs text-[#5C5A56] mt-0.5">{r[labelKey]}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // product or {} for new
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/products", { params: { limit: 200 } }).then((r) => setProducts(r.data));

  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const open = (p) => {
    setEditing(p || {});
    setForm(p ? { ...EMPTY, ...p } : EMPTY);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    try {
      if (editing?.id) {
        await api.put(`/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", form);
        toast.success("Product created");
      }
      close();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-2">Manage</p>
          <h1 className="font-display text-4xl text-[#2C2B29]">Products</h1>
        </div>
        <button
          data-testid="new-product-btn"
          onClick={() => open(null)}
          className="hh-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5"
        >
          <Plus size={14} weight="bold" /> New product
        </button>
      </div>

      <div className="border border-[#E5E0D8] rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#EADDC8]/40 text-left">
            <tr>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Title</th>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Category</th>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Price</th>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Featured</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-[#E5E0D8]">
                <td className="px-4 py-3 text-[#2C2B29]">{p.title}</td>
                <td className="px-4 py-3 text-[#5C5A56]">{p.category}</td>
                <td className="px-4 py-3 text-[#5C5A56]">{p.price || "—"}</td>
                <td className="px-4 py-3 text-[#5C5A56]">{p.featured ? "Yes" : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`edit-product-${p.id}`} onClick={() => open(p)} className="text-[#5B7B53] hover:text-[#D17A58] mr-3"><PencilSimple size={16} /></button>
                  <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="text-[#D17A58] hover:text-[#B5613F]"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#2C2B29]/50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full my-8 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#E5E0D8] sticky top-0 bg-white">
              <h2 className="font-display text-2xl text-[#2C2B29]">{editing.id ? "Edit product" : "New product"}</h2>
              <button onClick={close} className="p-2 hover:bg-[#EADDC8]/40 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Title</label>
                  <input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Category</label>
                  <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm">
                    <option value="">Select...</option>
                    {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Product image</label>
                  <ImageUpload
                    value={form.image_url}
                    onChange={(url) => setForm({ ...form, image_url: url })}
                    testid="product-image-upload"
                  />
                </div>
                <div className="sm:col-span-2">
                  <GalleryField value={form.gallery || []} mainImage={form.image_url} onChange={(g) => setForm({ ...form, gallery: g })} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Price (display)</label>
                  <input data-testid="form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="₹2,499" className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
                  <p className="text-[11px] text-[#5C5A56] mt-1">Lowering it 10%+ emails readers with a price alert on this product.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Amazon ASIN</label>
                  <input value={form.asin} onChange={(e) => setForm({ ...form, asin: e.target.value })} placeholder="B08C7KG5LP" className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Rating</label>
                  <input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Review count</label>
                  <input type="number" value={form.review_count} onChange={(e) => setForm({ ...form, review_count: parseInt(e.target.value || 0) })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Short description</label>
                <input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Long description</label>
                <textarea rows={3} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
              </div>
              <ArrayField label="Short benefits shown on cards (up to 3)" value={form.benefits} onChange={(v) => setForm({ ...form, benefits: v })} testid="form-benefits" />
              <div className="grid sm:grid-cols-2 gap-4">
                <ArrayField label="Pros" value={form.pros} onChange={(v) => setForm({ ...form, pros: v })} testid="form-pros" />
                <ArrayField label="Cons" value={form.cons} onChange={(v) => setForm({ ...form, cons: v })} testid="form-cons" />
                <ArrayField label="Features" value={form.features} onChange={(v) => setForm({ ...form, features: v })} testid="form-features" />
                <ArrayField label="Tags" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testid="form-tags" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#2C2B29]">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                Feature on homepage
              </label>
            </div>
            <div className="p-6 border-t border-[#E5E0D8] flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={close} className="px-5 py-2.5 rounded-full text-sm border border-[#E5E0D8] text-[#2C2B29]">Cancel</button>
              <button data-testid="form-save" onClick={save} className="hh-btn-primary rounded-full px-6 py-2.5 text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_LIST = { title: "", subtitle: "", category: "", hero_image: "", intro: "", product_ids: [], featured: false, item_notes: {} };

export function AdminLists() {
  const [lists, setLists] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_LIST);

  const load = () => api.get("/lists").then((r) => setLists(r.data));

  useEffect(() => {
    load();
    api.get("/products", { params: { limit: 200 } }).then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const open = (l) => { setEditing(l || {}); setForm(l ? { ...EMPTY_LIST, ...l } : EMPTY_LIST); };
  const close = () => { setEditing(null); setForm(EMPTY_LIST); };

  const save = async () => {
    try {
      if (editing?.id) await api.put(`/lists/${editing.id}`, form);
      else await api.post("/lists", form);
      toast.success("Saved");
      close();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this list?")) return;
    await api.delete(`/lists/${id}`);
    toast.success("Deleted");
    load();
  };

  const togglePid = (pid) => {
    setForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(pid) ? f.product_ids.filter((x) => x !== pid) : [...f.product_ids, pid],
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-2">Manage</p>
          <h1 className="font-display text-4xl text-[#2C2B29]">Curated lists</h1>
        </div>
        <button data-testid="new-list-btn" onClick={() => open(null)} className="hh-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus size={14} weight="bold" /> New list
        </button>
      </div>

      <div className="border border-[#E5E0D8] rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#EADDC8]/40 text-left">
            <tr>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Title</th>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Category</th>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Products</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {lists.map((l) => (
              <tr key={l.id} className="border-t border-[#E5E0D8]">
                <td className="px-4 py-3 text-[#2C2B29]">{l.title}</td>
                <td className="px-4 py-3 text-[#5C5A56]">{l.category}</td>
                <td className="px-4 py-3 text-[#5C5A56]">{l.product_ids.length}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`edit-list-${l.id}`} onClick={() => open(l)} className="text-[#5B7B53] hover:text-[#D17A58] mr-3"><PencilSimple size={16} /></button>
                  <button data-testid={`delete-list-${l.id}`} onClick={() => remove(l.id)} className="text-[#D17A58] hover:text-[#B5613F]"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#2C2B29]/50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full my-8 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#E5E0D8] sticky top-0 bg-white">
              <h2 className="font-display text-2xl text-[#2C2B29]">{editing.id ? "Edit list" : "New list"}</h2>
              <button onClick={close} className="p-2 hover:bg-[#EADDC8]/40 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Title</label>
                <input data-testid="list-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Subtitle</label>
                <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm">
                    <option value="">Select...</option>
                    {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Hero image</label>
                  <ImageUpload
                    value={form.hero_image}
                    onChange={(url) => setForm({ ...form, hero_image: url })}
                    testid="list-hero-upload"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Intro (editorial text)</label>
                <textarea rows={4} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Products in this list</label>
                <div className="border border-[#E5E0D8] rounded-lg max-h-72 overflow-y-auto divide-y divide-[#E5E0D8]">
                  {products.map((p) => (
                    <label key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-[#EADDC8]/30 cursor-pointer">
                      <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => togglePid(p.id)} />
                      <span className="text-[#2C2B29] flex-1">{p.title}</span>
                      <span className="text-xs text-[#5C5A56]">{p.category}</span>
                    </label>
                  ))}
                </div>
              </div>
              <ListItemNotes form={form} setForm={setForm} products={products} />
              <label className="flex items-center gap-2 text-sm text-[#2C2B29]">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                Feature on homepage
              </label>
            </div>
            <div className="p-6 border-t border-[#E5E0D8] flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={close} className="px-5 py-2.5 rounded-full text-sm border border-[#E5E0D8] text-[#2C2B29]">Cancel</button>
              <button data-testid="list-form-save" onClick={save} className="hh-btn-primary rounded-full px-6 py-2.5 text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminSettings() {
  const [form, setForm] = useState({ affiliate_tag: "", site_name: "", site_tagline: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-2">Configure</p>
      <h1 className="font-display text-4xl text-[#2C2B29] mb-8">Settings</h1>
      <form onSubmit={save} className="max-w-xl space-y-5 border border-[#E5E0D8] bg-white rounded-2xl p-8">
        <div>
          <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Amazon Associates tag</label>
          <input
            data-testid="settings-tag"
            value={form.affiliate_tag}
            onChange={(e) => setForm({ ...form, affiliate_tag: e.target.value })}
            className="w-full px-3 py-2.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5B7B53]/30"
          />
          <p className="text-xs text-[#5C5A56] mt-1.5">Applied to every Amazon link on the site.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Site name</label>
          <input
            value={form.site_name || ""}
            onChange={(e) => setForm({ ...form, site_name: e.target.value })}
            className="w-full px-3 py-2.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5B7B53]/30"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Tagline</label>
          <input
            value={form.site_tagline || ""}
            onChange={(e) => setForm({ ...form, site_tagline: e.target.value })}
            className="w-full px-3 py-2.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5B7B53]/30"
          />
        </div>
        <div className="pt-5 border-t border-[#E5E0D8] space-y-4" data-testid="settings-ads">
          <div>
            <h2 className="font-display text-xl text-[#2C2B29]">Google AdSense ad spots</h2>
            <p className="text-xs text-[#5C5A56] mt-1">In AdSense go to Ads → By ad unit, create an ad unit and copy its number (data-ad-slot). Leave empty to hide that ad spot.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">In-article ad unit ID (between blog paragraphs)</label>
            <input
              data-testid="settings-ad-slot-in-article"
              value={form.ad_slot_in_article || ""}
              onChange={(e) => setForm({ ...form, ad_slot_in_article: e.target.value })}
              placeholder="e.g. 1234567890"
              inputMode="numeric"
              className="w-full px-3 py-2.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5B7B53]/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Display ad unit ID (homepage, after Top picks)</label>
            <input
              data-testid="settings-ad-slot-display"
              value={form.ad_slot_display || ""}
              onChange={(e) => setForm({ ...form, ad_slot_display: e.target.value })}
              placeholder="e.g. 9876543210"
              inputMode="numeric"
              className="w-full px-3 py-2.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5B7B53]/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Show an ad inside articles after every</label>
            <select
              data-testid="settings-ad-interval"
              value={form.ad_interval || 3}
              onChange={(e) => setForm({ ...form, ad_interval: Number(e.target.value) })}
              className="w-full px-3 py-2.5 rounded-lg border border-[#E5E0D8] bg-white text-sm"
            >
              <option value={2}>2 paragraphs (more ads)</option>
              <option value={3}>3 paragraphs (recommended)</option>
            </select>
          </div>
        </div>
        <button data-testid="settings-save" disabled={saving} type="submit" className="hh-btn-primary rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60">
          {saving ? "Saving..." : "Save changes"}
        </button>
      </form>
    </div>
  );
}


const EMPTY_POST = { title: "", subtitle: "", cover_image: "", content: "", author: "HomeHearth Editors", tags: [], published: true, related_product_ids: [], section: "" };

export function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);

  const load = () => api.get("/posts").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const open = (p) => { setEditing(p || {}); setForm(p ? { ...EMPTY_POST, ...p, content: p.content || "" } : EMPTY_POST); };
  const close = () => { setEditing(null); setForm(EMPTY_POST); };

  const save = async () => {
    if (!form.title || !form.content) {
      toast.error("Title and content are required");
      return;
    }
    try {
      if (editing?.id) await api.put(`/posts/${editing.id}`, form);
      else await api.post("/posts", form);
      toast.success("Saved");
      close();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/posts/${id}`);
    toast.success("Deleted");
    load();
  };

  const fetchContent = async (id) => {
    const { data } = await api.get(`/posts/${id}`);
    setForm({ ...EMPTY_POST, ...data });
    setEditing(data);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-2">Manage</p>
          <h1 className="font-display text-4xl text-[#2C2B29]">Blog posts</h1>
        </div>
        <button data-testid="new-post-btn" onClick={() => open(null)} className="hh-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus size={14} weight="bold" /> New post
        </button>
      </div>

      <div className="border border-[#E5E0D8] rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#EADDC8]/40 text-left">
            <tr>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Title</th>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Author</th>
              <th className="px-4 py-3 font-medium text-[#5C5A56]">Published</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-[#E5E0D8]">
                <td className="px-4 py-3 text-[#2C2B29]">{p.title}</td>
                <td className="px-4 py-3 text-[#5C5A56]">{p.author}</td>
                <td className="px-4 py-3 text-[#5C5A56]">{p.published ? "Yes" : "Draft"}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`edit-post-${p.id}`} onClick={() => fetchContent(p.id)} className="text-[#5B7B53] hover:text-[#D17A58] mr-3"><PencilSimple size={16} /></button>
                  <button data-testid={`delete-post-${p.id}`} onClick={() => remove(p.id)} className="text-[#D17A58] hover:text-[#B5613F]"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#2C2B29]/50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full my-8 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#E5E0D8] sticky top-0 bg-white">
              <h2 className="font-display text-2xl text-[#2C2B29]">{editing.id ? "Edit post" : "New post"}</h2>
              <button onClick={close} className="p-2 hover:bg-[#EADDC8]/40 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Title</label>
                <input data-testid="post-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Subtitle</label>
                <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Blog section (optional)</label>
                <input data-testid="post-form-section" value={form.section || ""} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="e.g. Home Décor, Smart Living & Practical Indian Homes" className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Author</label>
                  <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Cover image</label>
                  <ImageUpload
                    value={form.cover_image}
                    onChange={(url) => setForm({ ...form, cover_image: url })}
                    testid="post-cover-upload"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Content (plain text; blank line = paragraph, **bold** supported)</label>
                <textarea data-testid="post-form-content" rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm font-mono leading-relaxed" />
              </div>
              <PostProductPicker form={form} setForm={setForm} />
              <ArrayField label="Tags" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testid="post-form-tags" />
              <label className="flex items-center gap-2 text-sm text-[#2C2B29]">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                Publish immediately (uncheck to save as draft)
              </label>
            </div>
            <div className="p-6 border-t border-[#E5E0D8] flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={close} className="px-5 py-2.5 rounded-full text-sm border border-[#E5E0D8] text-[#2C2B29]">Cancel</button>
              <button data-testid="post-form-save" onClick={save} className="hh-btn-primary rounded-full px-6 py-2.5 text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
